import { useEffect, useRef } from "react";
import type { PhaseType } from "../usePhaseAudio";

interface DivineLightProps {
  intensity: number;
  phase: PhaseType;
}

const PHASE_PALETTE: Record<PhaseType, { r: number; g: number; b: number; r2: number; g2: number; b2: number }> = {
  idle:        { r: 255, g: 240, b: 180, r2: 255, g2: 200, b2: 80  },
  "PHASE I":   { r: 200, g: 80,  b: 50,  r2: 160, g2: 40,  b2: 20  },
  "PHASE II":  { r: 220, g: 140, b: 30,  r2: 255, g2: 170, b2: 50  },
  "PHASE III": { r: 255, g: 245, b: 190, r2: 255, g2: 225, b2: 130 },
};

export default function DivineLight({ intensity, phase }: DivineLightProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const intensityRef = useRef(intensity);
  const phaseRef = useRef(phase);
  const currentColorRef = useRef({ ...PHASE_PALETTE.idle });

  useEffect(() => { intensityRef.current = intensity; }, [intensity]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // ── 天界の光の柱（太い） ──
    interface HeavenPillar {
      x: number;
      width: number;
      opacity: number;
      phase: number;
      speed: number;
    }
    const pillars: HeavenPillar[] = Array.from({ length: 7 }, (_, i) => ({
      x: (i / 7) * (canvas.width || 1920) + Math.random() * 100,
      width: 30 + Math.random() * 100,
      opacity: 0.015 + Math.random() * 0.04,
      phase: Math.random() * Math.PI * 2,
      speed: 0.004 + Math.random() * 0.006,
    }));

    // ── 天使のオーラ輪（頭上に浮かぶ光輪） ──
    interface HaloRing {
      x: number; y: number;
      r: number;
      opacity: number;
      phase: number;
      speed: number;
      tilt: number;
    }
    const halos: HaloRing[] = Array.from({ length: 4 }, () => ({
      x: (canvas.width || 1920) * (0.15 + Math.random() * 0.7),
      y: (canvas.height || 1080) * (0.1 + Math.random() * 0.4),
      r: 20 + Math.random() * 50,
      opacity: 0.04 + Math.random() * 0.08,
      phase: Math.random() * Math.PI * 2,
      speed: 0.003 + Math.random() * 0.005,
      tilt: (Math.random() - 0.5) * 0.4,
    }));

    // ── 精霊の光球（浮遊する小球） ──
    interface SpiritOrb {
      x: number; y: number;
      vx: number; vy: number;
      r: number;
      opacity: number;
      phase: number;
      phaseSpeed: number;
      hue: number;
    }
    const orbs: SpiritOrb[] = Array.from({ length: 10 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * (canvas.height || 1080),
      vx: (Math.random() - 0.5) * 0.3,
      vy: -(0.1 + Math.random() * 0.2),
      r: 3 + Math.random() * 8,
      opacity: 0.08 + Math.random() * 0.15,
      phase: Math.random() * Math.PI * 2,
      phaseSpeed: 0.01 + Math.random() * 0.015,
      hue: 40 + Math.random() * 25,
    }));

    // ── 光芒レイ（中央上部から放射） ──
    interface Ray {
      angle: number;
      speed: number;
      length: number;
      opacity: number;
      width: number;
    }
    const rays: Ray[] = Array.from({ length: 28 }, (_, i) => ({
      angle: (i / 28) * Math.PI * 2 + Math.random() * 0.15,
      speed: 0.0002 + Math.random() * 0.0003,
      length: 0.35 + Math.random() * 0.65,
      opacity: 0.015 + Math.random() * 0.055,
      width: 0.005 + Math.random() * 0.01,
    }));

    // ── 天界の羽根シルエット（ぼんやり） ──
    interface AngelWing {
      x: number; y: number;
      scale: number;
      opacity: number;
      phase: number;
      speed: number;
      vx: number; vy: number;
    }
    const wings: AngelWing[] = Array.from({ length: 3 }, () => ({
      x: (canvas.width || 1920) * (0.1 + Math.random() * 0.8),
      y: (canvas.height || 1080) * (0.05 + Math.random() * 0.5),
      scale: 0.4 + Math.random() * 0.7,
      opacity: 0.018 + Math.random() * 0.03,
      phase: Math.random() * Math.PI * 2,
      speed: 0.004 + Math.random() * 0.006,
      vx: (Math.random() - 0.5) * 0.06,
      vy: -(0.03 + Math.random() * 0.05),
    }));

    // ── 浮遊粒子 ──
    interface Mote {
      x: number; y: number;
      vx: number; vy: number;
      opacity: number;
      size: number;
      life: number; maxLife: number;
      r: number; g: number; b: number;
    }
    const motes: Mote[] = [];

    let t = 0;
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    const spawnMote = (col: { r: number; g: number; b: number }) => {
      const cx = (canvas.width || 1920) / 2;
      const cy = (canvas.height || 1080) * 0.38;
      const angle = Math.random() * Math.PI * 2;
      const dist = 20 + Math.random() * 180;
      motes.push({
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist * 0.5,
        vx: (Math.random() - 0.5) * 0.6,
        vy: -(0.2 + Math.random() * 0.8),
        opacity: 0,
        size: 0.5 + Math.random() * 2.5,
        life: 0,
        maxLife: 90 + Math.random() * 200,
        r: col.r, g: col.g, b: col.b,
      });
    };

    // ── 天使の羽根を描く ──
    function drawWing(
      ctx: CanvasRenderingContext2D,
      x: number, y: number, scale: number,
      opacity: number, phase: number,
      col: { r: number; g: number; b: number }
    ) {
      const flap = Math.sin(phase) * 0.12;
      const ws = 120 * scale;
      ctx.save();
      ctx.translate(x, y);
      ctx.globalAlpha = opacity;

      // 左翼
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.bezierCurveTo(-ws * 0.35, -ws * (0.28 + flap), -ws * 0.85, -ws * (0.08 + flap * 0.4), -ws, ws * 0.12);
      ctx.bezierCurveTo(-ws * 0.65, ws * 0.04, -ws * 0.28, -ws * 0.04, 0, 0);
      const lg = ctx.createLinearGradient(-ws, 0, 0, 0);
      lg.addColorStop(0, `rgba(${col.r},${col.g},${col.b},0)`);
      lg.addColorStop(0.5, `rgba(${col.r},${col.g},${col.b},${opacity * 0.5})`);
      lg.addColorStop(1, `rgba(${col.r},${col.g},${col.b},0)`);
      ctx.fillStyle = lg;
      ctx.fill();

      // 右翼
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.bezierCurveTo(ws * 0.35, -ws * (0.28 + flap), ws * 0.85, -ws * (0.08 + flap * 0.4), ws, ws * 0.12);
      ctx.bezierCurveTo(ws * 0.65, ws * 0.04, ws * 0.28, -ws * 0.04, 0, 0);
      const rg = ctx.createLinearGradient(0, 0, ws, 0);
      rg.addColorStop(0, `rgba(${col.r},${col.g},${col.b},0)`);
      rg.addColorStop(0.5, `rgba(${col.r},${col.g},${col.b},${opacity * 0.5})`);
      rg.addColorStop(1, `rgba(${col.r},${col.g},${col.b},0)`);
      ctx.fillStyle = rg;
      ctx.fill();

      // 光輪
      const haloR = ws * 0.16;
      const haloY = -ws * 0.42;
      const hg = ctx.createRadialGradient(0, haloY, 0, 0, haloY, haloR);
      hg.addColorStop(0, `rgba(${col.r},${col.g},${col.b},${opacity * 0.7})`);
      hg.addColorStop(0.6, `rgba(${col.r},${col.g},${col.b},${opacity * 0.2})`);
      hg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(0, haloY, haloR, 0, Math.PI * 2);
      ctx.fillStyle = hg;
      ctx.fill();
      // 光輪の縁
      ctx.beginPath();
      ctx.arc(0, haloY, haloR * 0.8, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},${opacity * 0.3})`;
      ctx.lineWidth = 0.4;
      ctx.stroke();

      ctx.globalAlpha = 1;
      ctx.restore();
    }

    function draw() {
      t++;
      const w = canvas.width;
      const h = canvas.height;
      const iv = intensityRef.current;
      const targetPalette = PHASE_PALETTE[phaseRef.current];
      const cc = currentColorRef.current;
      const lerpSpeed = 0.015;

      cc.r  = lerp(cc.r,  targetPalette.r,  lerpSpeed);
      cc.g  = lerp(cc.g,  targetPalette.g,  lerpSpeed);
      cc.b  = lerp(cc.b,  targetPalette.b,  lerpSpeed);
      cc.r2 = lerp(cc.r2, targetPalette.r2, lerpSpeed);
      cc.g2 = lerp(cc.g2, targetPalette.g2, lerpSpeed);
      cc.b2 = lerp(cc.b2, targetPalette.b2, lerpSpeed);

      const col = { r: Math.round(cc.r), g: Math.round(cc.g), b: Math.round(cc.b) };
      const col2 = { r: Math.round(cc.r2), g: Math.round(cc.g2), b: Math.round(cc.b2) };

      ctx.clearRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h * 0.38;

      // ══ PHASE I：地上の炎と血の霧 ══
      if (phaseRef.current === "PHASE I") {
        const fogGrd = ctx.createRadialGradient(cx, cy, 0, cx, h, h * 0.9);
        fogGrd.addColorStop(0, `rgba(${col.r},${Math.round(col.g * 0.2)},${Math.round(col.b * 0.1)},${0.07 * iv})`);
        fogGrd.addColorStop(0.5, `rgba(${Math.round(col.r * 0.4)},0,0,${0.04 * iv})`);
        fogGrd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = fogGrd;
        ctx.fillRect(0, 0, w, h);
      }

      // ══ PHASE II：炎の大地グロー ══
      if (phaseRef.current === "PHASE II") {
        const flameY = h * (0.72 + Math.sin(t * 0.015) * 0.04);
        const fGrd = ctx.createRadialGradient(cx, flameY, 0, cx, flameY, w * 0.65);
        fGrd.addColorStop(0, `rgba(${col.r},${Math.round(col.g * 0.55)},0,${0.09 * iv})`);
        fGrd.addColorStop(0.4, `rgba(${Math.round(col.r * 0.55)},${Math.round(col.g * 0.25)},0,${0.04 * iv})`);
        fGrd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = fGrd;
        ctx.fillRect(0, 0, w, h);
      }

      // ══ PHASE III：天界の光の海 ══
      if (phaseRef.current === "PHASE III") {
        const heavenGrd = ctx.createLinearGradient(0, 0, 0, h * 0.6);
        heavenGrd.addColorStop(0, `rgba(${col.r},${col.g},${col.b},${0.06 * iv})`);
        heavenGrd.addColorStop(0.5, `rgba(${col2.r},${col2.g},${col2.b},${0.03 * iv})`);
        heavenGrd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = heavenGrd;
        ctx.fillRect(0, 0, w, h);
      }

      // ══ 天界の光柱 ══
      pillars.forEach((p) => {
        p.phase += p.speed;
        const pulse = Math.sin(p.phase) * 0.5 + 0.5;
        const alpha = p.opacity * iv * (0.4 + pulse * 0.6);
        const pillarGrd = ctx.createLinearGradient(0, 0, 0, h * 0.7);
        pillarGrd.addColorStop(0, `rgba(${col.r},${col.g},${col.b},0)`);
        pillarGrd.addColorStop(0.2, `rgba(${col.r},${col.g},${col.b},${alpha})`);
        pillarGrd.addColorStop(0.7, `rgba(${col2.r},${col2.g},${col2.b},${alpha * 0.5})`);
        pillarGrd.addColorStop(1, `rgba(${col2.r},${col2.g},${col2.b},0)`);
        ctx.fillStyle = pillarGrd;
        ctx.fillRect(p.x - p.width / 2, 0, p.width, h * 0.7);
      });

      // ══ 天使の羽根シルエット ══
      wings.forEach((w_) => {
        w_.phase += w_.speed;
        w_.x += w_.vx;
        w_.y += w_.vy;
        if (w_.y < -150) { w_.y = h + 80; w_.x = Math.random() * w; }
        if (w_.x < -150) w_.x = w + 100;
        if (w_.x > w + 150) w_.x = -100;
        const wingAlpha = w_.opacity * iv * (0.5 + Math.sin(w_.phase * 0.7) * 0.5);
        drawWing(ctx, w_.x, w_.y, w_.scale, wingAlpha, w_.phase, col);
      });

      // ══ 精霊の光球 ══
      orbs.forEach((orb) => {
        orb.phase += orb.phaseSpeed;
        orb.x += orb.vx + Math.sin(orb.phase * 0.8) * 0.25;
        orb.y += orb.vy + Math.cos(orb.phase * 0.6) * 0.18;
        if (orb.y < -30) { orb.y = h + 20; orb.x = Math.random() * w; }
        if (orb.x < -20) { orb.x = w + 10; }
        if (orb.x > w + 20) { orb.x = -10; }
        const pulse = Math.sin(orb.phase) * 0.35 + 0.65;
        const orbAlpha = orb.opacity * iv * pulse;
        const orbGrd = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.r * 3.5);
        orbGrd.addColorStop(0, `hsla(${orb.hue}, 90%, 92%, ${orbAlpha})`);
        orbGrd.addColorStop(0.3, `hsla(${orb.hue}, 80%, 72%, ${orbAlpha * 0.55})`);
        orbGrd.addColorStop(0.7, `hsla(${orb.hue}, 70%, 60%, ${orbAlpha * 0.15})`);
        orbGrd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.r * 3.5, 0, Math.PI * 2);
        ctx.fillStyle = orbGrd;
        ctx.fill();
        // 輝点
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.r * pulse * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${orb.hue}, 100%, 96%, ${orbAlpha * 0.85})`;
        ctx.fill();
      });

      // ══ 光輪（天使のハロー） ══
      halos.forEach((h_) => {
        h_.phase += h_.speed;
        const hPulse = Math.sin(h_.phase) * 0.4 + 0.6;
        const hAlpha = h_.opacity * iv * hPulse;
        ctx.save();
        ctx.translate(h_.x, h_.y);
        ctx.scale(1, Math.cos(h_.tilt + Math.sin(h_.phase * 0.5) * 0.15) * 0.35 + 0.65);
        ctx.beginPath();
        ctx.arc(0, 0, h_.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},${hAlpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // 内輪
        ctx.beginPath();
        ctx.arc(0, 0, h_.r * 0.75, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},${hAlpha * 0.4})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.restore();
      });

      // ══ 光芒レイ ══
      const phaseMultiplier = phaseRef.current === "PHASE II" ? 1.6
        : phaseRef.current === "PHASE III" ? 0.7 : 1;
      rays.forEach((ray) => {
        ray.angle += ray.speed * phaseMultiplier;
        const len = h * ray.length * (0.4 + iv * 0.6);
        const ex = cx + Math.cos(ray.angle) * len;
        const ey = cy + Math.sin(ray.angle) * len;

        const rayGrd = ctx.createLinearGradient(cx, cy, ex, ey);
        const op = ray.opacity * iv * (phaseRef.current === "PHASE III" ? 1.5 : 1);
        rayGrd.addColorStop(0, `rgba(${col.r},${col.g},${col.b},${op})`);
        rayGrd.addColorStop(0.4, `rgba(${col2.r},${col2.g},${col2.b},${op * 0.4})`);
        rayGrd.addColorStop(1, `rgba(${col2.r},${col2.g},${col2.b},0)`);

        ctx.save();
        ctx.beginPath();
        const half = h * ray.width;
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(ray.angle - 0.05) * half, cy + Math.sin(ray.angle - 0.05) * half);
        ctx.lineTo(ex, ey);
        ctx.lineTo(cx + Math.cos(ray.angle + 0.05) * half, cy + Math.sin(ray.angle + 0.05) * half);
        ctx.closePath();
        ctx.fillStyle = rayGrd;
        ctx.fill();
        ctx.restore();
      });

      // ══ 中央グロー（神聖な光源） ══
      const glowPulse = phaseRef.current === "PHASE I"
        ? 1 + Math.sin(t * 0.025) * 0.3
        : phaseRef.current === "PHASE II"
        ? 1 + Math.sin(t * 0.035) * 0.4
        : 1 + Math.sin(t * 0.008) * 0.15;
      const glowSize = (80 + 35 * glowPulse) * (0.3 + iv * 0.7);
      const glowGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowSize * 3.2);
      glowGrd.addColorStop(0, `rgba(${col.r},${col.g},${col.b},${0.22 * iv})`);
      glowGrd.addColorStop(0.25, `rgba(${col2.r},${col2.g},${col2.b},${0.10 * iv})`);
      glowGrd.addColorStop(0.6, `rgba(${col2.r},${col2.g},${col2.b},${0.03 * iv})`);
      glowGrd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(cx, cy, glowSize * 3.2, 0, Math.PI * 2);
      ctx.fillStyle = glowGrd;
      ctx.fill();

      // ══ PHASE III：天界の光輪（外側） ══
      if (phaseRef.current === "PHASE III") {
        const ringR1 = (h * 0.28 + Math.sin(t * 0.007) * 18) * iv;
        const ringR2 = (h * 0.42 + Math.sin(t * 0.005 + 1) * 24) * iv;
        [ringR1, ringR2].forEach((rr, ri) => {
          const ringGrd = ctx.createRadialGradient(cx, cy, rr * 0.82, cx, cy, rr);
          ringGrd.addColorStop(0, `rgba(255,240,180,0)`);
          ringGrd.addColorStop(0.5, `rgba(255,235,160,${(0.04 - ri * 0.015) * iv})`);
          ringGrd.addColorStop(1, `rgba(255,220,100,0)`);
          ctx.beginPath();
          ctx.arc(cx, cy, rr, 0, Math.PI * 2);
          ctx.fillStyle = ringGrd;
          ctx.fill();
        });
        // 天使の羽根：PHASE IIIは明るく
        wings.forEach((w_) => {
          const wingAlpha = w_.opacity * iv * 2.2 * (0.5 + Math.sin(w_.phase * 0.7) * 0.5);
          drawWing(ctx, w_.x, w_.y, w_.scale * 1.3, Math.min(wingAlpha, 0.12), w_.phase, { r: 255, g: 240, b: 180 });
        });
      }

      // ══ 浮遊粒子 ══
      const spawnRate = phaseRef.current === "PHASE III" ? 0.3 : phaseRef.current === "PHASE II" ? 0.2 : 0.12;
      const maxMotes = phaseRef.current === "PHASE III" ? 100 : phaseRef.current === "PHASE II" ? 75 : 50;
      if (Math.random() < spawnRate * iv && motes.length < maxMotes) {
        spawnMote(col);
      }
      for (let i = motes.length - 1; i >= 0; i--) {
        const m = motes[i];
        m.life++;
        m.x += m.vx;
        m.y += m.vy * (phaseRef.current === "PHASE III" ? 1.5 : 1);
        const prog = m.life / m.maxLife;
        m.opacity = prog < 0.2
          ? (prog / 0.2) * 0.7 * iv
          : (1 - (prog - 0.2) / 0.8) * 0.7 * iv;
        if (m.life >= m.maxLife) { motes.splice(i, 1); continue; }
        if (m.opacity <= 0) continue;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${m.r},${m.g},${m.b},${m.opacity})`;
        ctx.fill();
        // 明るい粒子には十字輝き
        if (m.size > 1.5 && m.opacity > 0.2) {
          const cl = m.size * 2.5;
          ctx.beginPath();
          ctx.moveTo(m.x - cl, m.y); ctx.lineTo(m.x + cl, m.y);
          ctx.strokeStyle = `rgba(${m.r},${m.g},${m.b},${m.opacity * 0.4})`;
          ctx.lineWidth = 0.4;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(m.x, m.y - cl); ctx.lineTo(m.x, m.y + cl);
          ctx.stroke();
        }
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-0"
      style={{ width: "100vw", height: "100vh" }}
    />
  );
}
